import { useMemo } from 'react';
import { LocalStore, useLocalStore } from 'shared/helpers/Store';
import {
  PostingSearchCriteriaDTO,
  postingSearchCriteriaSchema,
} from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { tryParseJSON, tryStringifyJSON } from 'shared/utils/DataUtils';

const LOADBOARD_RECENT_SEARCHES_KEY = 'loadboard_recent_searches';
type RecentSearches = Array<[string, PostingSearchCriteriaDTO]>;

function listRecentSearches(): RecentSearches {
  const store = LocalStore.get(LOADBOARD_RECENT_SEARCHES_KEY);
  const result = store ? tryParseJSON<RecentSearches>(store) : undefined;
  return result || [];
}

export function useRecentSearches(): RecentSearches {
  const value = useLocalStore(LOADBOARD_RECENT_SEARCHES_KEY);
  return useMemo(() => {
    const result = value ? tryParseJSON<RecentSearches>(value) : undefined;

    if (!result) return [];

    return result
      .map(([key, search]) => {
        try {
          return [key, postingSearchCriteriaSchema.cast(search)];
        } catch {
          return null;
        }
      })
      .filter(Boolean) as RecentSearches;
  }, [value]);
}

export function addRecentSearch(searchCriteria: PostingSearchCriteriaDTO) {
  if (
    searchCriteria.pickup_venues.length === 0 &&
    searchCriteria.delivery_venues.length === 0
  ) {
    return;
  }

  const hash = generateHash(searchCriteria);
  const prevRecentSearches = listRecentSearches();
  const recentSearchesMap = new Map(prevRecentSearches);
  recentSearchesMap.delete(hash);
  const recentSearchesArray = [[hash, searchCriteria], ...recentSearchesMap];
  LocalStore.set(
    LOADBOARD_RECENT_SEARCHES_KEY,
    tryStringifyJSON(recentSearchesArray.slice(0, 3)) || null,
  );
}

export function generateHash(searchCriteria: PostingSearchCriteriaDTO) {
  return (
    searchCriteria.pickup_venues
      .map((x) => [x.state, x.city, x.zip].join('|'))
      .join(',') +
    '->' +
    searchCriteria.delivery_venues
      .map((x) => [x.state, x.city, x.zip].join('|'))
      .join(',')
  );
}
